import { useEffect, useState, lazy, Suspense, useCallback, useRef } from 'react';
import { useCanvas } from '../../contexts/CanvasContext';
import { useDesignerStore } from '../../stores';
import { useKeyboardShortcuts, useDesign, useAutoSave, useCart, useAnalytics, useTemplate } from '../../hooks';
import { useCanvasOperations } from '../../hooks/useCanvasOperations';
import { FabricCanvas } from '../canvas/FabricCanvas';
import { ZoomControls } from '../canvas/ZoomControls';
import { Toolbar } from '../toolbar/Toolbar';
import {
  TextPropertiesPanel,
  ObjectPropertiesPanel,
  LayersPanel,
  AlignmentTools,
  ImageFiltersPanel,
} from '../panels';
import { ToastContainer, ProgressIndicator, VariantSelector, LaserModeIndicator } from '../common';
import { apiClient } from '../../utils/api';
import type { ProgressStep } from '../common/ProgressIndicator';
import type { Template } from '../../types/template';
import { t } from '../../i18n';
import styles from './DesignerLayout.module.css';

// Lazy load modals for better performance
const QuoteModal = lazy(() => import('../modals/QuoteModal'));
const ShareModal = lazy(() => import('../modals/ShareModal'));
const ExportModal = lazy(() => import('../modals/ExportModal'));
const PricingTableModal = lazy(() => import('../modals/PricingTableModal'));

// Mobile breakpoint hook
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  return isMobile;
};

export const DesignerLayout = () => {
  const { isReady } = useCanvas(); // Initialize canvas context
  useKeyboardShortcuts(); // Set up keyboard shortcuts
  const analytics = useAnalytics(); // Initialize analytics
  const isMobile = useIsMobile();

  const { setProductContext, isLoading, loadingText, status, designId, productId, variantId, setLoading, selectedObjectIds } = useDesignerStore();
  const { saveDesign, loadDesign, isSaving } = useDesign();
  const { hasAutoSave, loadFromLocalStorage } = useAutoSave();
  const { addToCart, isAddingToCart } = useCart();
  const { loadTemplate } = useTemplate();
  const { addText, addImageFromUrl, deleteSelected } = useCanvasOperations();

  // File input ref for mobile image upload
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Modal states
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showPricingModal, setShowPricingModal] = useState(false);

  // Progress tracking
  const [currentStep, setCurrentStep] = useState<ProgressStep>(1);

  // Check if something is selected
  const hasSelection = selectedObjectIds.length > 0;

  // Handle mobile image upload
  const handleMobileImageUpload = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const dataUrl = event.target?.result as string;
        addImageFromUrl(dataUrl);
      };
      reader.readAsDataURL(file);
    }
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [addImageFromUrl]);

  // Parse URL parameters on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const urlProductId = params.get('productId') || getProductIdFromPath();
    const variantId = params.get('variantId') || undefined;
    const urlDesignId = params.get('designId') || undefined;

    if (urlProductId) {
      setProductContext(urlProductId, variantId, urlDesignId);
      analytics.trackPageView(window.location.pathname);
      analytics.trackFunnelStep(1, 'Design Started');
      // Track open to backend for daily reports
      analytics.trackOpenToBackend(urlProductId, variantId);
    }
  }, [setProductContext, analytics]);

  // Load product template on mount (wait for canvas to be ready)
  useEffect(() => {
    if (!productId || !isReady) return;

    const loadProductTemplate = async () => {
      setLoading(true, 'Loading template...');

      try {
        // Get template JSON from URL params (BigCommerce passes this)
        const params = new URLSearchParams(window.location.search);
        const templateParam = params.get('template');

        // Build endpoint with all query params
        const queryParams = new URLSearchParams();
        if (variantId) {
          queryParams.set('variantId', variantId);
        }
        if (templateParam) {
          queryParams.set('template', templateParam);
        }

        const endpoint = `/api/product/${productId}/template${queryParams.toString() ? '?' + queryParams.toString() : ''}`;

        const response = await apiClient.get<{ success: boolean; template?: Template; error?: string }>(endpoint);

        if (response.success && response.data?.template) {
          await loadTemplate(response.data.template);
        } else {
        }
      } catch (err) {
        console.error('Template loading error:', err);
      } finally {
        setLoading(false);
      }
    };

    loadProductTemplate();
  }, [productId, variantId, isReady, loadTemplate, setLoading]);

  // Load design or auto-save after template is loaded
  useEffect(() => {
    if (!productId) return;

    // Wait a bit for template to load first
    const timer = setTimeout(() => {
      const params = new URLSearchParams(window.location.search);
      const urlDesignId = params.get('designId');

      if (urlDesignId) {
        loadDesign(urlDesignId);
      } else if (hasAutoSave()) {
        // Silently restore auto-saved design
        loadFromLocalStorage();
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [productId, loadDesign, hasAutoSave, loadFromLocalStorage, analytics]);

  // Mobile zoom - fit canvas to screen
  useEffect(() => {
    if (!isMobile || !isReady) return;

    let debounceTimer: ReturnType<typeof setTimeout> | null = null;

    const calculateMobileZoom = () => {
      const padding = 20;
      const toolbarHeight = 60; // Mobile floating toolbar
      const orderBarHeight = 60; // Mobile order button
      const headerHeight = 56;

      const availableWidth = window.innerWidth - (padding * 2);
      const availableHeight = window.innerHeight - headerHeight - toolbarHeight - orderBarHeight - (padding * 2);

      const { canvasSize } = useDesignerStore.getState();

      // Guard against invalid canvas size (division by zero)
      if (!canvasSize || canvasSize.width <= 0 || canvasSize.height <= 0) {
        return;
      }

      const scaleX = availableWidth / canvasSize.width;
      const scaleY = availableHeight / canvasSize.height;
      const mobileZoom = Math.min(scaleX, scaleY, 1); // Never zoom beyond 100%

      useDesignerStore.getState().setZoom(mobileZoom);
    };

    // Debounced version for resize events
    const debouncedCalculateMobileZoom = () => {
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }
      debounceTimer = setTimeout(calculateMobileZoom, 150);
    };

    // Calculate immediately on mount
    calculateMobileZoom();

    // Recalculate on orientation change (debounced)
    window.addEventListener('resize', debouncedCalculateMobileZoom);
    return () => {
      window.removeEventListener('resize', debouncedCalculateMobileZoom);
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }
    };
  }, [isMobile, isReady]);

  const handleSave = async () => {
    await saveDesign();
    analytics.trackDesignSaved(designId || 'new');
  };

  const handleContinueToPricing = () => {
    setShowPricingModal(true);
    setCurrentStep(2);
    analytics.trackFunnelStep(2, 'Pricing Viewed');
  };

  const handleAddToCartFromPricing = async (quantity: number) => {
    const success = await addToCart();
    if (success) {
      analytics.trackAddToCart(productId || '', quantity);
      analytics.trackFunnelStep(3, 'Added to Cart');
      // Track add to cart to backend for daily reports
      const safeVariantId = variantId != null ? variantId : undefined;
      const safeDesignId = designId != null ? designId : undefined;
      analytics.trackAddToCartToBackend(productId || '', safeVariantId, undefined, safeDesignId, quantity);
      setCurrentStep(3);
      setShowPricingModal(false);
    }
  };

  const handleRequestQuote = () => {
    setShowQuoteModal(true);
    analytics.track({
      category: 'Lead',
      action: 'quote_modal_opened',
    });
  };

  const handleShare = () => {
    setShowShareModal(true);
    analytics.trackShareDesign('modal');
  };

  const handleExport = () => {
    setShowExportModal(true);
  };

  return (
    <div className={styles.layout}>
      {/* Progress Indicator */}
      <ProgressIndicator currentStep={currentStep} />

      {/* Toast Notifications */}
      <ToastContainer />

      {/* Header */}
      <header className={styles.header}>
        <div className={styles.headerLeft}>
          <button
            className={styles.backButton}
            onClick={() => window.history.back()}
            aria-label={t('design.back_aria', 'Go back')}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
            <span className={styles.hideOnMobile}>{t('design.back', 'Back')}</span>
          </button>
        </div>
        <div className={styles.headerCenter}>
          <h1 className={styles.productTitle}>{t('design.title', 'Design Your Product')}</h1>
          {productId && !isMobile && <VariantSelector productId={productId} />}
        </div>
        <div className={styles.headerRight}>
          <button
            className={styles.headerButton}
            onClick={handleShare}
            aria-label={t('design.share_aria', 'Share design')}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
              <polyline points="16 6 12 2 8 6" />
              <line x1="12" y1="2" x2="12" y2="15" />
            </svg>
            <span className={styles.hideOnMobile}>{t('design.share', 'Share')}</span>
          </button>
          <button
            className={styles.headerButton}
            onClick={handleExport}
            aria-label={t('design.export_aria', 'Export design')}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            <span className={styles.hideOnMobile}>{t('design.export', 'Export')}</span>
          </button>
          <button
            className={`${styles.headerButton} ${styles.primaryButton}`}
            onClick={handleSave}
            disabled={isSaving}
            aria-label={t('design.save_aria', 'Save design')}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
              <polyline points="17 21 17 13 7 13 7 21" />
              <polyline points="7 3 7 8 15 8" />
            </svg>
            <span className={styles.hideOnMobile}>{isSaving ? t('design.saving', 'Saving...') : t('design.save', 'Save')}</span>
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main className={styles.main}>
        {/* Left Toolbar - Desktop only */}
        {!isMobile && (
          <aside className={styles.toolbarContainer} role="toolbar" aria-label={t('design.tools_aria', 'Design tools')}>
            <Toolbar />
          </aside>
        )}

        {/* Canvas Area */}
        <div className={styles.canvasContainer} role="region" aria-label={t('design.canvas_aria', 'Design canvas')}>
          <div className={styles.canvasWrapper}>
            <FabricCanvas />
          </div>
          <LaserModeIndicator />
          {!isMobile && (
            <div className={styles.zoomControlsWrapper}>
              <ZoomControls />
            </div>
          )}
        </div>

        {/* Right Panel - Desktop only */}
        {!isMobile && (
          <aside className={styles.rightPanel} role="complementary" aria-label={t('design.properties_aria', 'Properties panel')}>
            <TextPropertiesPanel />
            <ObjectPropertiesPanel />
            <ImageFiltersPanel />
            <AlignmentTools />
            <LayersPanel />
          </aside>
        )}

        {/* Mobile Floating Toolbar */}
        {isMobile && (
          <>
            {/* Hidden file input for image upload */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              style={{ display: 'none' }}
            />

            {/* Floating toolbar with Add Text, Add Image, Delete */}
            <div className={styles.mobileFloatingToolbar}>
              <button
                className={styles.floatingToolButton}
                onClick={() => addText()}
                aria-label="Add Text"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="4 7 4 4 20 4 20 7" />
                  <line x1="9" y1="20" x2="15" y2="20" />
                  <line x1="12" y1="4" x2="12" y2="20" />
                </svg>
                <span>Text</span>
              </button>
              <button
                className={styles.floatingToolButton}
                onClick={handleMobileImageUpload}
                aria-label="Add Image"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                  <circle cx="8.5" cy="8.5" r="1.5" />
                  <polyline points="21 15 16 10 5 21" />
                </svg>
                <span>Image</span>
              </button>
              {hasSelection && (
                <button
                  className={`${styles.floatingToolButton} ${styles.deleteButton}`}
                  onClick={() => deleteSelected()}
                  aria-label="Delete"
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="3 6 5 6 21 6" />
                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                  </svg>
                  <span>Delete</span>
                </button>
              )}
            </div>
          </>
        )}
      </main>

      {/* Mobile Sticky Order Button */}
      {isMobile && (
        <div className={styles.mobileOrderBar}>
          <button
            className={styles.mobileOrderButton}
            onClick={handleContinueToPricing}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="9" cy="21" r="1" />
              <circle cx="20" cy="21" r="1" />
              <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
            </svg>
            <span>Continue to Order</span>
          </button>
        </div>
      )}

      {/* Footer - Desktop only */}
      {!isMobile && (
        <footer className={styles.footer}>
          <div className={styles.footerLeft}>
            <span className={styles.status} role="status" aria-live="polite">
              {status || t('design.status_ready', 'Ready')}
            </span>
          </div>
          <div className={styles.footerCenter}>
            <div className={styles.nextStepInfo}>
              <p className={styles.nextStepText}>
                {t('design.ready_to_order', 'Ready to order your custom design?')}
              </p>
            </div>
          </div>
          <div className={styles.footerRight}>
            <button
              className={styles.footerButton}
              onClick={handleRequestQuote}
            >
              {t('design.request_quote', 'Request Quote')}
            </button>
            <button
              className={`${styles.footerButton} ${styles.primaryButton} ${styles.continueButton}`}
              onClick={handleContinueToPricing}
            >
              <span>{t('design.continue_to_pricing', 'Continue to Pricing')}</span>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="5" y1="12" x2="19" y2="12" />
                <polyline points="12 5 19 12 12 19" />
              </svg>
            </button>
          </div>
        </footer>
      )}

      {/* Loading Overlay */}
      {isLoading && (
        <div className={styles.loadingOverlay} role="alert" aria-busy="true">
          <div className={styles.loadingSpinner} />
          <p>{loadingText || t('design.loading', 'Loading...')}</p>
        </div>
      )}

      {/* Lazy-loaded Modals */}
      <Suspense fallback={null}>
        {showQuoteModal && (
          <QuoteModal
            isOpen={showQuoteModal}
            onClose={() => setShowQuoteModal(false)}
            designId={designId}
          />
        )}
        {showShareModal && (
          <ShareModal
            isOpen={showShareModal}
            onClose={() => setShowShareModal(false)}
          />
        )}
        {showExportModal && (
          <ExportModal
            isOpen={showExportModal}
            onClose={() => setShowExportModal(false)}
          />
        )}
        {showPricingModal && (
          <PricingTableModal
            isOpen={showPricingModal}
            onClose={() => setShowPricingModal(false)}
            onAddToCart={handleAddToCartFromPricing}
            isAddingToCart={isAddingToCart}
          />
        )}
      </Suspense>
    </div>
  );
};

// Helper to extract productId from URL path like /design/123
function getProductIdFromPath(): string | null {
  const match = window.location.pathname.match(/\/design\/(\d+)/);
  return match?.[1] ?? null;
}

export default DesignerLayout;
